<template>
  <div class="ph-report">
    <div class="ph-report-bar">
      <label class="ph-field"><span>С</span><input type="date" v-model="dateStart"/></label>
      <label class="ph-field"><span>По</span><input type="date" v-model="dateEnd"/></label>
      <button type="button" class="ph-btn" :disabled="busy" @click="load">Показать</button>
      <button type="button" class="ph-btn ph-btn-ghost" :disabled="busy || !summary" @click="downloadXlsx">Скачать xlsx</button>
    </div>

    <p v-if="error" class="ph-err">{{ error }}</p>

    <div v-if="summary" class="ph-summary">
      <span>Переходов: <b>{{ summary.totalSessions }}</b> по {{ summary.totalPhrases }} фразам</span>
      <span>Желаемые: <b>{{ summary.goodSessions }}</b> · показались {{ summary.goodShown }} из {{ summary.goodTotal }}</span>
      <span>Нежелаемые: <b>{{ summary.badSessions }}</b> · поймано {{ summary.badCaught }} из {{ summary.badTotal }}</span>
      <span>Вне списков: <b>{{ summary.otherSessions }}</b></span>
    </div>

    <div v-if="summary" class="ph-tabs">
      <button type="button" class="ph-tab" :class="{ 'ph-tab-on': tab === 'rows' }" @click="tab = 'rows'">Фразы за период</button>
      <button type="button" class="ph-tab" :class="{ 'ph-tab-on': tab === 'list' }" @click="tab = 'list'">По спискам</button>
    </div>

    <ag-grid-vue
      v-if="summary"
      class="ag-theme-quartz ph-grid"
      :columnDefs="tab === 'rows' ? rowCols : listCols"
      :rowData="tab === 'rows' ? rows : listRows"
      :defaultColDef="defaultColDef"
      :localeText="localeText"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

export default {
  name: 'PhraseReport',
  components: { AgGridVue },
  props: {
    projectId: { type: String, default: '' },
    setId: { type: String, default: '' },
    setName: { type: String, default: '' },
  },
  data() {
    return {
      hostApi: process.env.VUE_APP_API_URL,
      dateStart: '',
      dateEnd: '',
      rows: [],
      listRows: [],
      summary: null,
      tab: 'rows',
      busy: false,
      error: '',
      defaultColDef: { sortable: true, filter: true, resizable: true },
      // Переводит только меню фильтра в столбцах — остального текста
      // (пагинация, контекстное меню) в этой таблице не показывается.
      localeText: {
        filterOoo: 'Фильтр...',
        empty: 'Выбрать',
        equals: 'Равно',
        notEqual: 'Не равно',
        lessThan: 'Меньше',
        greaterThan: 'Больше',
        inRange: 'Между',
        inRangeStart: 'От',
        inRangeEnd: 'До',
        lessThanOrEqual: 'Меньше или равно',
        greaterThanOrEqual: 'Больше или равно',
        contains: 'Содержит',
        notContains: 'Не содержит',
        startsWith: 'Начинается с',
        endsWith: 'Заканчивается на',
        blank: 'Пусто',
        notBlank: 'Не пусто',
        before: 'До',
        after: 'После',
        andCondition: 'И',
        orCondition: 'ИЛИ',
        applyFilter: 'Применить',
        clearFilter: 'Очистить',
        resetFilter: 'Сбросить',
        cancelFilter: 'Отмена',
        textFilter: 'Текстовый фильтр',
        numberFilter: 'Числовой фильтр',
        dateFilter: 'Фильтр по дате',
        setFilter: 'Фильтр по значениям',
      },
      rowCols: [
        { headerName: 'Фраза (utm_term)', field: 'phrase', flex: 2 },
        { headerName: 'Переходы', field: 'sessions', width: 110 },
        { headerName: 'Категория', field: 'category', width: 140 },
        { headerName: 'Желаемая фраза', field: 'goodPhrase', flex: 1 },
        { headerName: 'Оценка (жел.)', field: 'goodMark', width: 130 },
        { headerName: 'Частотность', field: 'goodFreq', width: 120 },
        { headerName: 'Нежелаемая фраза', field: 'badPhrase', flex: 1 },
        { headerName: 'Оценка (нежел.)', field: 'badMark', width: 140 },
      ],
      listCols: [
        { headerName: 'Фраза списка', field: 'value', flex: 2 },
        { headerName: 'Список', field: 'list', width: 140 },
        { headerName: 'Переходов', field: 'sessions', width: 120 },
        { headerName: 'Фраз совпало', field: 'phrases', width: 140 },
        { headerName: 'Оценка', field: 'mark', width: 110 },
        { headerName: 'Частотность', field: 'freq', width: 120 },
      ],
    };
  },
  mounted() {
    // По умолчанию — последние 7 дней, самый частый запрос.
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 6 * 86400000);
    this.dateEnd = today.toISOString().slice(0, 10);
    this.dateStart = weekAgo.toISOString().slice(0, 10);
  },
  methods: {
    body() {
      return {
        clientId: localStorage.getItem('clientId'),
        projectId: this.projectId,
        setId: this.setId,
        dateStart: this.dateStart,
        dateEnd: this.dateEnd,
      };
    },
    async load() {
      if (!this.setId) return;
      this.busy = true;
      this.error = '';
      try {
        const res = await axios.post(this.hostApi + '/api/v1/phraseset/report', this.body());
        const data = res.data.data;
        this.rows = data.rows;
        this.listRows = data.listRows;
        this.summary = data.summary;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.error) || 'Не удалось построить отчёт';
      } finally {
        this.busy = false;
      }
    },
    async downloadXlsx() {
      this.busy = true;
      try {
        const res = await axios.post(this.hostApi + '/api/v1/phraseset/reportXlsx', this.body(), {
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'фразы ' + this.setName + ' ' + this.dateStart + '_' + this.dateEnd + '.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.error = 'Не удалось скачать файл';
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.ph-report { margin-top: 28px; }
.ph-report-bar { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.ph-field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
.ph-summary { display: flex; flex-wrap: wrap; gap: 18px; margin: 14px 0; font-size: 13px; }
.ph-tabs { display: flex; gap: 6px; margin-bottom: 8px; }
.ph-tab { padding: 5px 12px; border: 1px solid currentColor; background: transparent; cursor: pointer; border-radius: 4px 4px 0 0; opacity: 0.6; }
.ph-tab-on { opacity: 1; font-weight: 600; }
.ph-grid { width: 100%; height: 520px; }
.ph-btn { padding: 5px 12px; border: 1px solid currentColor; background: transparent; cursor: pointer; border-radius: 4px; }
.ph-btn[disabled] { opacity: 0.5; cursor: default; }
.ph-btn-ghost { opacity: 0.8; }
.ph-err { color: #b02a37; font-size: 13px; }
</style>
